import type {
  AudioPlaybackRepository,
  MediaSessionMeta,
  MediaSkipHandlers,
} from '@/domain/Repositories';
import type { PlaybackSnapshot } from '@/domain/Models';
import { getAudioObjectUrl } from './AudioStore';

type Listener = (snapshot: PlaybackSnapshot) => void;

export class AudioPlaybackRepositoryImpl implements AudioPlaybackRepository {
  private audio: HTMLAudioElement | null = null;
  private objectUrl: string | null = null;
  private prefetchCache = new Map<string, string>();
  private listeners = new Set<Listener>();
  private skipHandlers: MediaSkipHandlers | null = null;
  private sessionMeta: MediaSessionMeta | null = null;
  private advanceHandler: (() => void | Promise<void>) | null = null;
  private loopEnabled = false;
  private stopping = false;
  private playGeneration = 0;
  private lastPositionSyncAt = 0;
  private current: PlaybackSnapshot = {
    assetId: null,
    state: 'IDLE',
    positionMs: 0,
    durationMs: 0,
    speed: 1,
    errorCode: null,
  };

  snapshot(): PlaybackSnapshot {
    return { ...this.current };
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }

  setMediaSession(meta: MediaSessionMeta | null): void {
    this.sessionMeta = meta;
    this.syncMediaSession();
  }

  setSkipHandlers(handlers: MediaSkipHandlers | null): void {
    this.skipHandlers = handlers;
    this.syncMediaSession();
  }

  setAdvanceHandler(handler: (() => void | Promise<void>) | null): void {
    this.advanceHandler = handler;
  }

  setLoop(enabled: boolean): void {
    this.loopEnabled = enabled;
    if (this.audio) this.audio.loop = false;
  }

  async prefetch(assetId: string): Promise<void> {
    if (!assetId || this.prefetchCache.has(assetId)) return;
    const url = await getAudioObjectUrl(assetId);
    if (url) this.prefetchCache.set(assetId, url);
  }

  private async resolveUrl(assetId: string): Promise<string | null> {
    const cached = this.prefetchCache.get(assetId);
    if (cached) return cached;
    const url = await getAudioObjectUrl(assetId);
    if (url) this.prefetchCache.set(assetId, url);
    return url;
  }

  private revokeUnusedUrl(url: string | null): void {
    if (!url) return;
    if ([...this.prefetchCache.values()].includes(url)) return;
    try {
      URL.revokeObjectURL(url);
    } catch {
      // node tests / already revoked
    }
  }

  private emit(): void {
    const snap = this.snapshot();
    for (const l of this.listeners) l(snap);
  }

  private mediaSession(): MediaSession | null {
    return typeof navigator !== 'undefined' && 'mediaSession' in navigator
      ? navigator.mediaSession
      : null;
  }

  private bindAction(
    ms: MediaSession,
    name: MediaSessionAction,
    fn: MediaSessionActionHandler | null,
  ): void {
    try {
      ms.setActionHandler(name, fn);
    } catch {
      // iOS / older WebKit may reject unsupported actions.
    }
  }

  private syncMediaSession(): void {
    const ms = this.mediaSession();
    if (!ms) return;
    if (this.sessionMeta) {
      const payload = {
        title: this.sessionMeta.title,
        artist: this.sessionMeta.artist ?? '储能英语实战',
        album: this.sessionMeta.album ?? '文章磨耳朵',
        artwork: this.sessionMeta.artworkUrl
          ? [{ src: this.sessionMeta.artworkUrl, sizes: '192x192', type: 'image/png' }]
          : [],
      };
      ms.metadata =
        typeof MediaMetadata !== 'undefined' ? new MediaMetadata(payload) : (payload as MediaMetadata);
    } else {
      ms.metadata = null;
    }
    this.bindAction(ms, 'play', () => {
      void this.resume();
    });
    this.bindAction(ms, 'pause', () => {
      void this.pause();
    });
    this.bindAction(ms, 'seekbackward', () => {
      void this.seekTo(Math.max(0, this.current.positionMs - 10_000));
    });
    this.bindAction(ms, 'seekforward', () => {
      void this.seekTo(this.current.positionMs + 10_000);
    });
    this.bindAction(ms, 'seekto', (details) => {
      if (details.seekTime != null) void this.seekTo(details.seekTime * 1000);
    });
    this.bindAction(
      ms,
      'previoustrack',
      this.skipHandlers ? () => { void this.skipHandlers!.onPrevious(); } : null,
    );
    this.bindAction(
      ms,
      'nexttrack',
      this.skipHandlers ? () => { void this.skipHandlers!.onNext(); } : null,
    );
    ms.playbackState =
      this.current.state === 'PLAYING'
        ? 'playing'
        : this.current.state === 'PAUSED' || this.current.state === 'BUFFERING'
          ? 'paused'
          : 'none';
    try {
      if (this.current.durationMs > 0) {
        ms.setPositionState({
          duration: this.current.durationMs / 1000,
          playbackRate: this.current.speed || 1,
          position: Math.min(this.current.positionMs, this.current.durationMs) / 1000,
        });
      }
    } catch {
      // duration not ready
    }
  }

  private clearMediaSession(): void {
    const ms = this.mediaSession();
    if (!ms) return;
    ms.metadata = null;
    ms.playbackState = 'none';
    for (const name of [
      'play',
      'pause',
      'seekbackward',
      'seekforward',
      'seekto',
      'previoustrack',
      'nexttrack',
    ] as MediaSessionAction[]) {
      this.bindAction(ms, name, null);
    }
  }

  private ensureAudio(): HTMLAudioElement {
    if (!this.audio) {
      this.audio = new Audio();
      this.audio.preload = 'auto';
      this.audio.addEventListener('timeupdate', () => {
        this.current.positionMs = Math.floor((this.audio?.currentTime ?? 0) * 1000);
        this.emit();
        const now = Date.now();
        if (now - this.lastPositionSyncAt >= 1000) {
          this.lastPositionSyncAt = now;
          this.syncMediaSession();
        }
      });
      this.audio.addEventListener('loadedmetadata', () => {
        this.current.durationMs = Math.floor((this.audio?.duration ?? 0) * 1000);
        this.emit();
        this.syncMediaSession();
      });
      this.audio.addEventListener('ended', () => {
        if (this.stopping) return;
        if (this.current.state === 'IDLE' || this.current.state === 'BUFFERING') return;
        this.current.positionMs = this.current.durationMs;
        this.current.state = 'COMPLETED';
        this.emit();
        this.syncMediaSession();
        if (this.loopEnabled) {
          void this.restartLoop();
          return;
        }
        if (this.advanceHandler) void this.advanceHandler();
      });
      this.audio.addEventListener('error', () => {
        this.current.state = 'FAILED';
        this.current.errorCode = 'AUDIO_PLAYBACK_FAILED';
        this.emit();
        this.syncMediaSession();
      });
    }
    return this.audio;
  }

  private async restartLoop(): Promise<void> {
    const audio = this.audio;
    if (!audio || !this.loopEnabled) return;
    audio.currentTime = 0;
    this.current.positionMs = 0;
    try {
      await audio.play();
      this.current.state = 'PLAYING';
      this.current.errorCode = null;
      this.emit();
      this.syncMediaSession();
    } catch {
      this.current.state = 'FAILED';
      this.current.errorCode = 'AUDIO_PLAY_BLOCKED';
      this.emit();
      this.syncMediaSession();
    }
  }

  async play(assetId: string, speed: number): Promise<void> {
    const generation = ++this.playGeneration;
    const url = await this.resolveUrl(assetId);
    if (generation !== this.playGeneration) return;
    if (!url) {
      this.current = {
        assetId,
        state: 'FAILED',
        positionMs: 0,
        durationMs: 0,
        speed,
        errorCode: 'AUDIO_NOT_FOUND',
      };
      this.emit();
      return;
    }
    const previousUrl = this.objectUrl;
    this.objectUrl = url;
    const audio = this.ensureAudio();
    this.current = {
      assetId,
      state: 'BUFFERING',
      positionMs: 0,
      durationMs: 0,
      speed,
      errorCode: null,
    };
    this.emit();
    audio.loop = false;
    audio.src = url;
    audio.playbackRate = speed;
    if (previousUrl && previousUrl !== url) this.revokeUnusedUrl(previousUrl);
    try {
      await audio.play();
      if (generation !== this.playGeneration) return;
      this.current.state = 'PLAYING';
      this.emit();
      this.syncMediaSession();
    } catch {
      if (generation !== this.playGeneration) return;
      this.current.state = 'FAILED';
      this.current.errorCode = 'AUDIO_PLAY_BLOCKED';
      this.emit();
      this.syncMediaSession();
    }
  }

  async pause(): Promise<void> {
    this.audio?.pause();
    if (this.current.state === 'PLAYING' || this.current.state === 'BUFFERING') {
      this.current.state = 'PAUSED';
      this.emit();
      this.syncMediaSession();
    }
  }

  async resume(): Promise<void> {
    if (!this.audio) return;
    if (this.current.state !== 'PAUSED' && this.current.state !== 'BUFFERING') return;
    try {
      await this.audio.play();
      this.current.state = 'PLAYING';
      this.current.speed = this.audio.playbackRate;
      this.emit();
      this.syncMediaSession();
    } catch {
      this.current.state = 'FAILED';
      this.current.errorCode = 'AUDIO_PLAY_BLOCKED';
      this.emit();
      this.syncMediaSession();
    }
  }

  async seekTo(positionMs: number): Promise<void> {
    if (!this.audio) return;
    this.audio.currentTime = Math.max(0, positionMs / 1000);
    this.current.positionMs = positionMs;
    this.emit();
    this.syncMediaSession();
  }

  async stop(): Promise<void> {
    this.stopping = true;
    this.playGeneration += 1;
    if (this.audio) {
      this.audio.pause();
      this.audio.removeAttribute('src');
      this.audio.load();
    }
    this.revokeUnusedUrl(this.objectUrl);
    this.objectUrl = null;
    for (const url of this.prefetchCache.values()) {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // node tests / already revoked
      }
    }
    this.prefetchCache.clear();
    this.current = {
      assetId: null,
      state: 'IDLE',
      positionMs: 0,
      durationMs: 0,
      speed: 1,
      errorCode: null,
    };
    this.clearMediaSession();
    this.emit();
    this.stopping = false;
  }
}
